"use client";

import { env } from "@/env";
import posthog from "posthog-js";
import type { Properties } from "posthog-js";

let hasWarnedMissingKey = false;
let hasWarnedCaptureDisabledOutsideProduction = false;

const ensurePostHogConfigured = () => {
  const configuredAppUrl = env.NEXT_PUBLIC_APP_URL?.trim();
  if (
    process.env.NODE_ENV !== "production" ||
    !configuredAppUrl ||
    typeof window === "undefined"
  ) {
    if (!hasWarnedCaptureDisabledOutsideProduction) {
      hasWarnedCaptureDisabledOutsideProduction = true;
      console.error(
        "[posthog] client analytics disabled outside production origin.",
        {
          nodeEnv: process.env.NODE_ENV ?? null,
          currentOrigin:
            typeof window !== "undefined" ? window.location.origin : null,
          expectedOrigin: configuredAppUrl ?? null,
        },
      );
    }
    return false;
  }

  try {
    const expectedOrigin = new URL(configuredAppUrl).origin;
    if (window.location.origin !== expectedOrigin) {
      if (!hasWarnedCaptureDisabledOutsideProduction) {
        hasWarnedCaptureDisabledOutsideProduction = true;
        console.error(
          "[posthog] client analytics disabled: current origin does not match production app origin.",
          {
            currentOrigin: window.location.origin,
            expectedOrigin,
          },
        );
      }
      return false;
    }
  } catch (error) {
    if (!hasWarnedCaptureDisabledOutsideProduction) {
      hasWarnedCaptureDisabledOutsideProduction = true;
      console.error(
        "[posthog] invalid NEXT_PUBLIC_APP_URL; analytics disabled.",
        {
          configuredAppUrl,
          error,
        },
      );
    }
    return false;
  }

  if (env.NEXT_PUBLIC_POSTHOG_KEY) return true;
  if (!hasWarnedMissingKey) {
    hasWarnedMissingKey = true;
    console.error(
      "[posthog] NEXT_PUBLIC_POSTHOG_KEY is not set; skipping analytics capture.",
    );
  }
  return false;
};

const identifyUser = (userId: string, properties?: Record<string, unknown>) => {
  if (!ensurePostHogConfigured()) return;
  try {
    posthog.identify(userId, properties);
  } catch (error) {
    console.error("[posthog] identify failed", {
      userId,
      properties,
      error,
    });
  }
};

const aliasUser = (alias: string) => {
  if (!ensurePostHogConfigured()) return;
  try {
    posthog.alias(alias);
  } catch (error) {
    console.error("[posthog] aliasUser failed", {
      alias,
      error,
    });
  }
};

export const captureEvent = (
  event: string,
  properties?: Record<string, unknown>,
) => {
  if (!ensurePostHogConfigured()) return;
  try {
    posthog.capture(event, properties);
  } catch (error) {
    console.error("[posthog] captureEvent failed", {
      event,
      properties,
      error,
    });
  }
};

export const captureExceptionClient = (
  error: unknown,
  additionalProperties?: Properties,
) => {
  if (!ensurePostHogConfigured()) return;
  try {
    posthog.captureException(error, additionalProperties);
  } catch (captureError) {
    console.error("[posthog] captureExceptionClient failed", {
      originalError: error,
      additionalProperties,
      captureError,
    });
  }
};

export const getDistinctId = (): string | undefined => {
  if (!ensurePostHogConfigured()) return undefined;
  try {
    return posthog.get_distinct_id();
  } catch (error) {
    console.error("[posthog] getDistinctId failed", { error });
    return undefined;
  }
};

export const registerProperties = (props: Record<string, unknown>) => {
  if (!ensurePostHogConfigured()) return;
  try {
    posthog.register(props);
  } catch (error) {
    console.error("[posthog] registerProperties failed", {
      props,
      error,
    });
  }
};

const resetPosthog = () => {
  if (!ensurePostHogConfigured()) return;
  try {
    posthog.reset();
  } catch (error) {
    console.error("[posthog] reset failed", { error });
  }
};
